import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import RichText from "@/components/RichText";
import VideoPlayer from "@/components/VideoPlayer";
import { publicUrl } from "@/lib/storage";

type Post = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  body: string | null;
  cover_image_path: string | null;
  image_paths: string[];
  video_urls: string[];
  published_at: string | null;
};

const SpotlightPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      const { data } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug)
        .eq("published", true)
        .maybeSingle();
      setPost((data as any) ?? null);
      setLoading(false);
    })();
  }, [slug]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="py-16 md:py-24">
        <article className="container max-w-3xl">
          <Link to="/spotlight" className="text-xs font-semibold uppercase tracking-[0.2em] text-primary hover:underline">
            ← Athlete Spotlight
          </Link>

          {loading ? (
            <p className="text-muted-foreground mt-8">Loading…</p>
          ) : !post ? (
            <p className="text-muted-foreground mt-8">Post not found.</p>
          ) : (
            <>
              <h1 className="text-4xl md:text-6xl mt-4 text-balance">{post.title}</h1>
              {post.subtitle && (
                <p className="text-xl text-muted-foreground mt-3">{post.subtitle}</p>
              )}
              {post.published_at && (
                <p className="text-xs text-muted-foreground mt-4">
                  {new Date(post.published_at).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              )}

              {post.cover_image_path && (
                <div className="mt-8 rounded-2xl overflow-hidden bg-muted">
                  <img src={publicUrl(post.cover_image_path)!} alt={post.title} className="w-full h-auto" />
                </div>
              )}

              {post.body && <RichText html={post.body} className="mt-8" />}

              {post.video_urls.length > 0 && (
                <div className="mt-8 space-y-6">
                  {post.video_urls.map((u, i) => (
                    <VideoPlayer key={i} url={u} />
                  ))}
                </div>
              )}

              {post.image_paths.length > 0 && (
                <div className="mt-8 grid sm:grid-cols-2 gap-4">
                  {post.image_paths.map((p, i) => {
                    const url = publicUrl(p);
                    if (!url) return null;
                    return (
                      <div key={i} className="rounded-2xl overflow-hidden bg-muted">
                        <img src={url} alt="" className="w-full h-auto" />
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default SpotlightPost;
